import { useCallback, useEffect, useState } from 'react';
import { apiFetch, apiUploadFile, getAccessToken, API_PREFIX } from '@/lib/api-client';
import type {
  AddressValidationPanelsResponse,
  AddressValidationQueueItem,
  SaveAddressValidationRequest,
  AddressValidationStatus,
  AuditLogItem,
  UniticketWriteResult,
  UniticketReopenReport,
  PublicAttachmentItem,
  TicketUpdateItem,
  FakeOdfPlan,
  FakeOdfAttachResult,
  FacadeBoxesResponse,
  EnrichmentSsvTsa,
  SsvTsaBatchResponse,
  BuildFileHit,
  BuildFilesBatchResponse,
  OpenOrderItem,
  OpenOrdersBatchResponse,
  UnlinkPreview,
  UnlinkTicketResult,
  DmpConsoleMappingResponse,
  AddressCommentItem,
  DmpConsoleMappingRow,
  DmpConsoleMappingWriteRequest,
  SameAddressUnitDto,
  SameAddressUnitsResponse,
  TicketResolvePreview,
  DmpStreetSearchItem,
} from '@uniticket/shared';

// The save/escalate endpoints return the refreshed panels plus the best-effort Uniticket write-back
// outcome (3.1/3.3/3.4) so the UI can report what happened on the Uniticket side.
type ValidationActionResponse = { data: AddressValidationPanelsResponse; uniticketSync?: UniticketWriteResult };

// Internal-only — the Validation Window endpoints (gated server-side to Unifiber staff).

// N45 (plan validé le 20-10-2026) — l'adresse du ticket que vise une demande « par adresse » (l'onglet ouvert) ;
// absente = l'adresse principale, comme avant.
const jvidQs = (jvid?: number | null) => (jvid != null ? `?jvid=${jvid}` : '');

export async function fetchAddressValidation(ticketId: string, jvid?: number | null) {
  return apiFetch<{ data: AddressValidationPanelsResponse }>(
    `/tickets/${ticketId}/address-validation${jvidQs(jvid)}`,
  );
}

/** N45 — ajouter une adresse au ticket : la réponse est la fenêtre ouverte SUR elle. Refus nommés (409 / 404 + code). */
export async function addTicketAddress(ticketId: string, jvid: number) {
  return apiFetch<{ data: AddressValidationPanelsResponse }>(`/tickets/${ticketId}/addresses`, {
    method: 'POST',
    body: JSON.stringify({ jvid }),
  });
}

/** N45 — retirer une adresse AJOUTÉE : la réponse est la fenêtre sur l'adresse principale. */
export async function removeTicketAddress(ticketId: string, jvid: number) {
  return apiFetch<{ data: AddressValidationPanelsResponse }>(`/tickets/${ticketId}/addresses/${jvid}`, { method: 'DELETE' });
}

/** N45 — « Résoudre le ticket » : ce qui l'empêche encore, et le récapitulatif proposé. */
export async function fetchResolvePreview(ticketId: string) {
  return apiFetch<{ data: TicketResolvePreview }>(`/tickets/${ticketId}/resolve-preview`);
}

/** N45 — UN commentaire (le récapitulatif relu), puis RESOLVED dans Uniticket. */
export async function resolveTicket(ticketId: string, recap: string) {
  return apiFetch<ValidationActionResponse>(`/tickets/${ticketId}/resolve`, {
    method: 'POST',
    body: JSON.stringify({ recap }),
  });
}

/** N45 — la recherche par rue de la console DMP (rue ≥ 3 caractères, numéro facultatif), pour « Ajouter une adresse ». */
export async function searchDmpStreet(street: string, number?: string | null) {
  const params = new URLSearchParams({ street: street.trim() });
  if (number?.trim()) params.set('number', number.trim());
  return apiFetch<{ data: DmpStreetSearchItem[] }>(`/dmp/address-search?${params}`);
}

/** The Uniticket ticket's attachments (metadata) — files/images the operator attached, surfaced read-only. */
export async function fetchTicketAttachments(ticketId: string) {
  return apiFetch<{ data: PublicAttachmentItem[] }>(`/tickets/${ticketId}/attachments`);
}

/** Upload a reviewer file onto the Uniticket ticket (≤ 10 MB) — sent right after a successful Save. */
export async function uploadTicketAttachment(ticketId: string, file: File) {
  const qs = `filename=${encodeURIComponent(file.name)}&mime=${encodeURIComponent(file.type || '')}`;
  return apiUploadFile<{ data: PublicAttachmentItem | null }>(`/tickets/${ticketId}/attachments?${qs}`, file);
}

/** Same payload but Panel 1 reference (and MDU list) re-fetched live from the DMP. */
export async function refreshAddressValidationReference(ticketId: string, jvid?: number | null) {
  return apiFetch<{ data: AddressValidationPanelsResponse }>(
    `/tickets/${ticketId}/address-validation/reference${jvidQs(jvid)}`,
  );
}

export async function saveAddressValidation(ticketId: string, body: SaveAddressValidationRequest) {
  return apiFetch<ValidationActionResponse>(
    `/tickets/${ticketId}/address-validation`,
    { method: 'POST', body: JSON.stringify(body) },
  );
}

/** Escalate: post a comment on the Uniticket ticket (when writes are enabled) + mark the verdict ESCALATED. */
export async function escalateAddressValidation(ticketId: string, remark?: string | null) {
  return apiFetch<ValidationActionResponse>(
    `/tickets/${ticketId}/address-validation/escalate`,
    { method: 'POST', body: JSON.stringify({ remark: remark ?? null }) },
  );
}

/** Return an escalation (F4): hand the file back to the reviewer who escalated it, with the answer.
 *  Server-guarded — only the current recipient of a live escalation may call it. */
export async function returnEscalatedValidation(ticketId: string, remark?: string | null) {
  return apiFetch<ValidationActionResponse>(
    `/tickets/${ticketId}/address-validation/return`,
    { method: 'POST', body: JSON.stringify({ remark: remark ?? null }) },
  );
}

/** Send to Build: post a "Pour avis Build" internal note on the Uniticket ticket (when writes are enabled) +
 *  park the address as WAITING_BUILD (it leaves the pending queue until a verdict is given). The optional
 *  operatorQuestion rides along as a PUBLIC comment to the operator (user case 2026-08-03). */
export async function sendToBuildAddressValidation(
  ticketId: string,
  remark?: string | null,
  operatorQuestion?: string | null,
) {
  return apiFetch<ValidationActionResponse>(
    `/tickets/${ticketId}/address-validation/send-to-build`,
    { method: 'POST', body: JSON.stringify({ remark: remark ?? null, operatorQuestion: operatorQuestion ?? null }) },
  );
}

// ── Address Release (writes public.address on the local copy) ─────────────────────────────────────
export interface ReleaseAddressResult {
  jvid: number;
  deliveryStatus: number | null;
  lengthEstimateFromStreet: number | null;
  deliveryStatusFromSibling: boolean;
  lengthFromSibling: boolean;
  /** 'YES_ESTIMATED_DISTANCE' when the 7xxx + >28 m quote rule fired on this release, else null. */
  quoteRequired: string | null;
  /** Fake-ODF safety-net outcome: a released address with no fiber gets its placeholder attached. */
  fakeOdf?: { created: boolean; utac?: string | null; reason?: string };
}

/** Release a DMP address — flip it to client-visible. The two non-fixed recipe fields may be supplied;
 *  omit them to copy from a released sibling server-side. Server refuses unless ADDR_TABLE_TARGET=local. */
export async function releaseAddress(
  jvid: number,
  body: { deliveryStatus?: number | null; lengthEstimateFromStreet?: number | null },
) {
  return apiFetch<{ data: ReleaseAddressResult }>(
    `/addresses/${jvid}/release`,
    { method: 'POST', body: JSON.stringify(body) },
  );
}

/** Un-release: revert a Release (back to not-visible / amber). Server refuses unless ADDR_TABLE_TARGET=local. */
export async function unreleaseAddress(jvid: number) {
  return apiFetch<{ data: { jvid: number } }>(
    `/addresses/${jvid}/unrelease`,
    { method: 'POST' },
  );
}

/** Clear one scope-list date on a DMP address. 'v15' = deployment list (also stamps out_of_scope_at) is
 *  the ONLY tier: since 2026-08-28 an address may never be removed from the build list (v2), and the type
 *  is how that is enforced client-side. The date is not recomputable: the way back is the action log. */
export async function removeFromScopeList(jvid: number, tier: 'v15') {
  return apiFetch<{ data: { jvid: number; tier: 'v15'; previousDate: string | null; outOfScopeStamped: boolean; updated: number } }>(
    `/addresses/${jvid}/scope-list/${tier}/remove`,
    { method: 'POST' },
  );
}

/** One (city, municipality) pair a postal code is known to cover, straight from the DMP referential.
 *  `common` is false for the long tail of encoding noise — still returned, so a genuine small hamlet is
 *  never silently turned into "unknown" (the threshold lives server-side, see listLocalitiesForPostalCode). */
export interface PostalCodeLocality {
  city: string;
  municipality: string;
  count: number;
  common: boolean;
}

/** What a postal code maps to. `total` 0 = the DMP knows nothing about it — that is "no answer", NOT
 *  "the city is wrong": outside Unifiber's footprint the check must stay silent. */
export async function fetchPostalCodeLocalities(postalCode: string) {
  return apiFetch<{ data: { postalCode: string; total: number; localities: PostalCodeLocality[] } }>(
    `/addresses/postal-code/${encodeURIComponent(postalCode)}/localities`,
  );
}

/** The identity fields an admin may correct from the DMP console. The core five must stay non-empty
 *  (they are NOT NULL in prod); '' is a legitimate value for the box and the extension. */
/** Set an address's dwelling unit type on the DMP -- ONE row, on demand. Never called for siblings and
 *  never in bulk: a building of three units where two are out of scope is a SDU, so no count decides the
 *  type. Reviewers + admins, address-lock guarded server-side. */
export async function updateDwellingUnitType(jvid: number, dwellingUnitType: 'SDU' | 'MDU') {
  return apiFetch<{ data: { jvid: number; previous: string | null; next: string; updated: number } }>(
    `/addresses/${jvid}/dwelling-type`,
    { method: 'POST', body: JSON.stringify({ dwellingUnitType }) },
  );
}

/** Correct an address's delivery status WITHOUT releasing it. Writes that one column (plus
 *  quote_required, which the 28 m rule derives from it) and leaves the other nine of the release recipe
 *  alone. Reviewers + admins, address-lock guarded server-side. */
export async function updateDeliveryStatus(jvid: number, deliveryStatus: number) {
  return apiFetch<{ data: { jvid: number; previous: number | null; next: number; quoteRequired: string | null; updated: number } }>(
    `/addresses/${jvid}/delivery-status`,
    { method: 'POST', body: JSON.stringify({ deliveryStatus }) },
  );
}

/** Écrire `status` SEUL — action d'EXCEPTION, console DMP uniquement (décision user 2026-08-31).
 *  Le statut se déduit normalement des actions (Release / Un-release l'écrivent avec le reste) ; cette
 *  route existe parce qu'AUCUNE action ne sait poser ni retirer HOMES_CONNECT. Le motif est obligatoire
 *  côté serveur : il part dans le journal d'actions avec l'avant/après. */
export async function updateAddressStatus(
  jvid: number, status: 'NOT_AVAILABLE' | 'HOMES_PASSED' | 'HOMES_CONNECT', reason: string,
) {
  return apiFetch<{ data: { jvid: number; previous: string | null; next: string; updated: number } }>(
    `/addresses/${jvid}/status`,
    { method: 'POST', body: JSON.stringify({ status, reason }) },
  );
}

export interface DeriveNetworkIdsResponse {
  jvid: number;
  areaId: { value: number; from: 'polygon' | 'sibling' | 'street' } | null;
  pop: { areaPopId: number; centralPopId: number; from: 'sibling' | 'neighbour'; sourceJvid: number } | null;
  unresolved: string[];
  updated: number;
}

/** Combler area_id / area_pop_id / central_pop_id en les DÉDUISANT : frère du même bâtiment, puis
 *  voisin de même parité, puis rien. Ne remplit que les trous (COALESCE côté serveur). */
export async function deriveNetworkIds(jvid: number) {
  return apiFetch<{ data: DeriveNetworkIdsResponse }>(
    `/addresses/${jvid}/derive-network-ids`, { method: 'POST' },
  );
}

export interface EditableAddressBody {
  streetName?: string;
  houseNumber?: string;
  houseNumberExtension?: string;
  busNumber?: string;
  postalCode?: string;
  municipality?: string;
  city?: string;
}

/** Correct a DMP address's identity (DMP console, SYSTEM_ADMIN only, address-lock guarded server-side).
 *  Send ONLY the fields that moved — the journal records the edit field by field. */
export async function updateAddressIdentity(jvid: number, body: EditableAddressBody) {
  // `target` says WHICH table took it: a JVID on Proximus's blacklist is written to their mapping row,
  // never to public.address (decision 2026-09-01). The screen reports what the server did, it does not
  // re-derive it — the two could then disagree.
  return apiFetch<{ data: { jvid: number; changed: string[]; updated: number; target: 'address' | 'mapping' } }>(
    `/dmp/console/addresses/${jvid}`,
    { method: 'PATCH', body: JSON.stringify(body) },
  );
}

// N39 — la table de mapping d'un JVID dans la console DMP, hors ticket. La lecture est ouverte à tous ceux qui ont
// la console ; l'écriture est réservée aux admins (le serveur le garde, l'écran ne fait que ne pas offrir le bouton).
export async function fetchConsoleMapping(jvid: number) {
  return apiFetch<{ data: DmpConsoleMappingResponse }>(`/dmp/address/${jvid}/mapping`);
}

// Les motifs laissés sur une adresse (22-10) — lecture seule, même public que la console.
export async function fetchAddressComments(jvid: number) {
  return apiFetch<{ data: AddressCommentItem[] }>(`/dmp/address/${jvid}/comments`);
}

export async function writeConsoleMapping(jvid: number, body: DmpConsoleMappingWriteRequest) {
  return apiFetch<{ data: { row: DmpConsoleMappingRow; edits: Record<string, { from: string | null; to: string | null }> } }>(
    `/dmp/console/addresses/${jvid}/mapping`,
    { method: 'PUT', body: JSON.stringify(body) },
  );
}

export interface BusNumberSwapSide { jvid: number; from: string; to: string }

/** Exchange two addresses' bus numbers in ONE server-side transaction (DMP console, SYSTEM_ADMIN only,
 *  both jvids lock-guarded). Not expressible as two identity edits: the first one would 409 against the
 *  seven-field twin check, on a state that only exists mid-correction. */
export async function swapBusNumbers(jvidA: number, jvidB: number) {
  return apiFetch<{ data: { a: BusNumberSwapSide; b: BusNumberSwapSide; updated: number } }>(
    '/dmp/console/addresses/swap-bus-number',
    { method: 'POST', body: JSON.stringify({ jvidA, jvidB }) },
  );
}

/** Ce qu'une pose de fake ODF ÉCRIRAIT sur une adresse existante — rien n'est écrit par cet appel.
 *  `popId` force le PoP au lieu de le déduire : c'est l'aperçu d'un choix manuel. */
export async function fetchFakeOdfPlan(jvid: number, popId?: number) {
  return apiFetch<{ data: FakeOdfPlan }>(
    `/dmp/console/addresses/${jvid}/fake-odf${popId != null ? `?popId=${popId}` : ''}`,
  );
}

/** Poser le placeholder fake ODF (utac + address_fiber sur le slot 0/0/0 du PoP) sur une adresse
 *  existante — console DMP, SYSTEM_ADMIN, verrou d'adresse côté serveur. On renvoie TOUJOURS le popId
 *  que l'aperçu a montré : l'écriture ne doit pas pouvoir atterrir sur un autre PoP que l'écran.
 *  Le serveur refuse visiblement (409) là où le filet du release se contentait de ne rien faire. */
export async function attachFakeOdf(jvid: number, popId: number) {
  return apiFetch<{ data: FakeOdfAttachResult }>(
    `/dmp/console/addresses/${jvid}/fake-odf`,
    { method: 'POST', body: JSON.stringify({ popId }) },
  );
}

export interface MappedBusNumber { jvid: number; operatorId: number; operatorLabel: string; busNumber: string | null }

/** The box numbers the OPERATORS declared for these jvids (read-only — we never rewrite their
 *  submission). Shown next to a swap because DMP "1"/"2" are Proximus "03"/"04". */
export async function fetchMappedBusNumbers(jvids: number[]) {
  return apiFetch<{ data: MappedBusNumber[] }>(
    `/dmp/console/mapped-bus-numbers?jvids=${jvids.join(',')}`,
  );
}

/** One earlier swap of the SAME pair, seen from jvidA: its box went aFrom → aTo, jvidB's the other way round. */
export interface BusNumberSwapRecord { swappedAt: string; by: string | null; aFrom: string; aTo: string }

/** Earlier swaps between exactly these two addresses, newest first — read from our audit trail, so a box
 *  moved outside the app is not in it. Unlike fetchMappedBusNumbers the server does NOT answer [] on
 *  failure: the modal must be able to tell "never swapped" from "could not check". */
export async function fetchBusNumberSwaps(jvidA: number, jvidB: number) {
  return apiFetch<{ data: BusNumberSwapRecord[] }>(
    `/dmp/console/bus-number-swaps?jvidA=${jvidA}&jvidB=${jvidB}`,
  );
}

/** N23 — the COCON facade boxes behind these JVIDs. About a second on the real DMP: fetched when the
 *  « En savoir plus » modal opens, never with the panels. */
export async function fetchFacadeBoxes(jvids: number[]) {
  return apiFetch<{ data: FacadeBoxesResponse }>(`/addresses/facade-boxes?jvids=${jvids.join(',')}`);
}

/** La lecture COCON d'un ticket, telle que la page la partage : pastilles, détail, « En savoir plus » et
 *  formulaire d'ajout lisent CET état, jamais leur propre lecture — c'est ce qui garantit un seul nombre. */
export interface FacadeCoconState {
  status: 'idle' | 'loading' | 'ready' | 'error';
  data: FacadeBoxesResponse | null;
  retry: () => void;
}

/** La route plafonne à 200 JVID par appel. */
const FACADE_BOXES_BATCH = 200;

/** Deux lots lisent le même COCON au même instant : un boîtier revenu deux fois est le même boîtier. */
function mergeFacadeResponses(parts: FacadeBoxesResponse[]): FacadeBoxesResponse {
  const boxes = new Map<string, FacadeBoxesResponse['boxes'][number]>();
  const elsewhere = new Map<number, FacadeBoxesResponse['linkedElsewhere'][number]>();
  const unlinked = new Set<number>();
  const addresses = new Map<number, FacadeBoxesResponse['addresses'][number]>();
  for (const p of parts) {
    for (const b of p.boxes) if (!boxes.has(b.name)) boxes.set(b.name, b);
    for (const l of p.linkedElsewhere) if (!elsewhere.has(l.jvid)) elsewhere.set(l.jvid, l);
    for (const j of p.unlinked) unlinked.add(j);
    for (const a of p.addresses) if (!addresses.has(a.jvid)) addresses.set(a.jvid, a);
  }
  return { boxes: [...boxes.values()], linkedElsewhere: [...elsewhere.values()], unlinked: [...unlinked], addresses: [...addresses.values()] };
}

/**
 * COCON pour les adresses QD / QDX d'un ticket (C0.1, 17-09) : UNE lecture par jeu de JVID, en tâche de fond,
 * jamais dans l'appel des panneaux — elle coûte 1,1 à 2,0 s mesurées en prod le 17-09. La clé est la liste
 * triée des JVID : un nouveau tableau aux mêmes valeurs ne relit rien, une boîte ajoutée (nouveau JVID) relit.
 */
export function useFacadeCocon(jvids: number[]): FacadeCoconState {
  const key = [...new Set(jvids.filter((j) => Number.isInteger(j) && j > 0))].sort((a, b) => a - b).join(',');
  const [state, setState] = useState<{ status: FacadeCoconState['status']; data: FacadeBoxesResponse | null }>(
    { status: key ? 'loading' : 'idle', data: null },
  );
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    if (!key) { setState({ status: 'idle', data: null }); return; }
    let cancelled = false;
    // Les données d'avant sont jetées : elles décrivaient peut-être d'autres JVID, et un nombre périmé
    // afficherait une place qui n'existe plus.
    setState({ status: 'loading', data: null });
    const ids = key.split(',').map(Number);
    const batches: number[][] = [];
    for (let i = 0; i < ids.length; i += FACADE_BOXES_BATCH) batches.push(ids.slice(i, i + FACADE_BOXES_BATCH));
    Promise.all(batches.map((b) => fetchFacadeBoxes(b)))
      .then((res) => { if (!cancelled) setState({ status: 'ready', data: mergeFacadeResponses(res.map((r) => r.data)) }); })
      .catch(() => { if (!cancelled) setState({ status: 'error', data: null }); });
    return () => { cancelled = true; };
  }, [key, attempt]);

  const retry = useCallback(() => setAttempt((n) => n + 1), []);
  return { ...state, retry };
}

export async function fetchSsvTsa(jvids: number[]) {
  return apiFetch<{ data: SsvTsaBatchResponse }>(`/addresses/ssv-tsa?jvids=${jvids.join(',')}`);
}

/** La lecture SSV / TSA d'un ticket, telle que la page la partage entre ses pastilles (17-09). `byJvid` reste nul
 *  pendant la lecture ET en cas d'échec : une lecture ratée n'a pas de réponse à donner, la pastille disparaît. */
export interface SsvTsaState {
  status: 'idle' | 'loading' | 'ready' | 'error';
  byJvid: Record<string, EnrichmentSsvTsa[]> | null;
  retry: () => void;
}

/** La route SSV / TSA plafonne, elle aussi, à 200 JVID par appel. */
const SSV_TSA_BATCH = 200;

/**
 * SSV / TSA pour tous les JVID d'un ticket (demande du user, 17-09) — lu comme COCON : une fois par jeu de JVID,
 * en tâche de fond, jamais dans l'appel des panneaux. Clé = JVID triés ; « Actualiser » relit par `retry`.
 */
export function useSsvTsa(jvids: number[]): SsvTsaState {
  const key = [...new Set(jvids.filter((j) => Number.isInteger(j) && j > 0))].sort((a, b) => a - b).join(',');
  const [state, setState] = useState<{ status: SsvTsaState['status']; byJvid: SsvTsaState['byJvid'] }>(
    { status: key ? 'loading' : 'idle', byJvid: null },
  );
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    if (!key) { setState({ status: 'idle', byJvid: null }); return; }
    let cancelled = false;
    setState({ status: 'loading', byJvid: null });
    const ids = key.split(',').map(Number);
    const batches: number[][] = [];
    for (let i = 0; i < ids.length; i += SSV_TSA_BATCH) batches.push(ids.slice(i, i + SSV_TSA_BATCH));
    Promise.all(batches.map((b) => fetchSsvTsa(b)))
      .then((res) => {
        if (!cancelled) setState({ status: 'ready', byJvid: Object.assign({}, ...res.map((r) => r.data.byJvid)) });
      })
      .catch(() => { if (!cancelled) setState({ status: 'error', byJvid: null }); });
    return () => { cancelled = true; };
  }, [key, attempt]);

  const retry = useCallback(() => setAttempt((n) => n + 1), []);
  return { ...state, retry };
}

export async function fetchBuildFilesFor(jvids: number[]) {
  return apiFetch<{ data: BuildFilesBatchResponse }>(`/addresses/build-files?jvids=${jvids.join(',')}`);
}

/** Les fichiers Build (service file, deployment list) des JVID d'un ticket, tels que la page les partage entre ses
 *  pastilles (22-10). Même contrat que SSV / TSA : `byJvid` nul pendant la lecture ET en cas d'échec. */
export interface BuildFilesState {
  status: 'idle' | 'loading' | 'ready' | 'error';
  byJvid: Record<string, BuildFileHit[]> | null;
  retry: () => void;
}

/** La route plafonne, elle aussi, à 200 JVID par appel. */
const BUILD_FILES_BATCH = 200;

/** Les pastilles « Service file » / « Deployment list » de la console, dans la fenêtre de validation (22-10) :
 *  une lecture par jeu de JVID, en tâche de fond, comme SSV / TSA. */
export function useBuildFiles(jvids: number[]): BuildFilesState {
  const key = [...new Set(jvids.filter((j) => Number.isInteger(j) && j > 0))].sort((a, b) => a - b).join(',');
  const [state, setState] = useState<{ status: BuildFilesState['status']; byJvid: BuildFilesState['byJvid'] }>(
    { status: key ? 'loading' : 'idle', byJvid: null },
  );
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    if (!key) { setState({ status: 'idle', byJvid: null }); return; }
    let cancelled = false;
    setState({ status: 'loading', byJvid: null });
    const ids = key.split(',').map(Number);
    const batches: number[][] = [];
    for (let i = 0; i < ids.length; i += BUILD_FILES_BATCH) batches.push(ids.slice(i, i + BUILD_FILES_BATCH));
    Promise.all(batches.map((b) => fetchBuildFilesFor(b)))
      .then((res) => {
        if (!cancelled) setState({ status: 'ready', byJvid: Object.assign({}, ...res.map((r) => r.data.byJvid)) });
      })
      .catch(() => { if (!cancelled) setState({ status: 'error', byJvid: null }); });
    return () => { cancelled = true; };
  }, [key, attempt]);

  const retry = useCallback(() => setAttempt((n) => n + 1), []);
  return { ...state, retry };
}

export async function fetchOpenOrders(jvids: number[]) {
  return apiFetch<{ data: OpenOrdersBatchResponse }>(`/addresses/open-orders?jvids=${jvids.join(',')}`);
}

/** Les ordres en cours d'un ticket (N42), tels que la page les partage entre ses caddies. `byJvid` reste nul
 *  pendant la lecture ET en cas d'échec — mais ici l'échec doit se DIRE : un caddie absent se lirait « aucun
 *  ordre, je peux toucher ce logement ». C'est `status === 'error'` qui porte la note de l'écran. */
export interface OpenOrdersState {
  status: 'idle' | 'loading' | 'ready' | 'error';
  byJvid: Record<string, OpenOrderItem[]> | null;
  retry: () => void;
}

/** La route des ordres en cours plafonne, elle aussi, à 200 JVID par appel. */
const OPEN_ORDERS_BATCH = 200;

/**
 * Les ordres en cours de tous les JVID d'un ticket (N42) — lus comme SSV / TSA : une fois par jeu de JVID, en
 * tâche de fond, jamais dans l'appel des panneaux. Clé = JVID triés ; « Rafraîchir depuis le DMP » relit par `retry`.
 */
export function useOpenOrders(jvids: number[]): OpenOrdersState {
  const key = [...new Set(jvids.filter((j) => Number.isInteger(j) && j > 0))].sort((a, b) => a - b).join(',');
  const [state, setState] = useState<{ status: OpenOrdersState['status']; byJvid: OpenOrdersState['byJvid'] }>(
    { status: key ? 'loading' : 'idle', byJvid: null },
  );
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    if (!key) { setState({ status: 'idle', byJvid: null }); return; }
    let cancelled = false;
    setState({ status: 'loading', byJvid: null });
    const ids = key.split(',').map(Number);
    const batches: number[][] = [];
    for (let i = 0; i < ids.length; i += OPEN_ORDERS_BATCH) batches.push(ids.slice(i, i + OPEN_ORDERS_BATCH));
    Promise.all(batches.map((b) => fetchOpenOrders(b)))
      .then((res) => {
        if (!cancelled) setState({ status: 'ready', byJvid: Object.assign({}, ...res.map((r) => r.data.byJvid)) });
      })
      .catch(() => { if (!cancelled) setState({ status: 'error', byJvid: null }); });
    return () => { cancelled = true; };
  }, [key, attempt]);

  const retry = useCallback(() => setAttempt((n) => n + 1), []);
  return { ...state, retry };
}

export async function fetchSameAddressUnits(ticketId: string, jvid?: number | null) {
  return apiFetch<{ data: SameAddressUnitsResponse }>(`/tickets/${ticketId}/same-address-units${jvidQs(jvid)}`);
}

/** N41 — les logements de la même adresse postale rangés sous un AUTRE building ID, pour la fenêtre d'un ticket.
 *  `units` reste vide pendant la lecture ET en cas d'échec : c'est `status === 'error'` qui fait dire à l'écran
 *  « impossible de vérifier » — une liste vide muette se lirait « aucun logement ailleurs ». */
export interface SameAddressState {
  status: 'idle' | 'loading' | 'ready' | 'error';
  units: SameAddressUnitDto[];
  truncated: boolean;
  retry: () => void;
}

const NO_UNITS: SameAddressUnitDto[] = [];

/**
 * La lecture N41 d'un ticket — comme COCON et les ordres en cours : UNE fois par état de la fenêtre, en tâche de
 * fond, jamais dans l'appel des panneaux. La clé porte les JVID de la fenêtre : une boîte ajoutée, un logement
 * relié relisent ; un nouveau tableau aux mêmes valeurs ne relit rien. « Rafraîchir depuis le DMP » relit par `retry`.
 */
export function useSameAddressUnits(
  ticketId: string | undefined,
  jvids: number[],
  // N45 — l'onglet ouvert : la lecture porte sur SON bâtiment.
  activeJvid?: number | null,
): SameAddressState {
  const ids = [...new Set(jvids.filter((j) => Number.isInteger(j) && j > 0))].sort((a, b) => a - b).join(',');
  const key = ticketId && ids ? `${ticketId}|${ids}|${activeJvid ?? ''}` : '';
  const [state, setState] = useState<Omit<SameAddressState, 'retry'>>(
    { status: key ? 'loading' : 'idle', units: NO_UNITS, truncated: false },
  );
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    if (!key) { setState({ status: 'idle', units: NO_UNITS, truncated: false }); return; }
    let cancelled = false;
    setState({ status: 'loading', units: NO_UNITS, truncated: false });
    const [tid, , active] = key.split('|');
    fetchSameAddressUnits(tid, active ? Number(active) : null)
      .then((res) => { if (!cancelled) setState({ status: 'ready', units: res.data.units, truncated: res.data.truncated }); })
      .catch(() => { if (!cancelled) setState({ status: 'error', units: NO_UNITS, truncated: false }); });
    return () => { cancelled = true; };
  }, [key, attempt]);

  const retry = useCallback(() => setAttempt((n) => n + 1), []);
  return { ...state, retry };
}

export interface SiblingMappingBody {
  addrStreet?: string | null;
  addrHouseNumber?: string | null;
  houseNumberExtension?: string | null;
  addrBusNumber?: string | null;
  addrPostalCode?: string | null;
  addrMunicipality?: string | null;
  addrCity?: string | null;
  addrDwellingUnitType?: string | null;
  edits?: Record<string, { from: string | null; to: string | null }>;
}

/** Post-release "manage address": upsert the operator mapping row for a building sibling (jvid) from the
 *  ticket's operator. Writes the local mapping copy on staging (ADDR_MAPPING_TARGET=local). */
export async function upsertSiblingMapping(ticketId: string, jvid: number, body: SiblingMappingBody) {
  return apiFetch<{ data: { upserted: boolean } }>(
    `/tickets/${ticketId}/addresses/${jvid}/mapping`,
    { method: 'POST', body: JSON.stringify(body) },
  );
}

export interface SiblingMappingRow {
  streetName: string | null;
  houseNumber: string | null;
  houseNumberExtension: string | null;
  busNumber: string | null;
  postalCode: string | null;
  municipality: string | null;
  city: string | null;
}

/** Read the existing operator mapping for a building sibling (jvid) — used to pre-fill the post-release
 *  "manage address" form so saved edits are visible on re-open. Returns null when no mapping row exists. */
export async function fetchSiblingMapping(ticketId: string, jvid: number) {
  return apiFetch<{ data: SiblingMappingRow | null }>(
    `/tickets/${ticketId}/addresses/${jvid}/mapping`,
  );
}

export async function fetchAddressValidationQueue(
  status?: AddressValidationStatus,
  includeTest = false,
  search?: string,
  view?: 'ticketResolved' | 'escalatedToMe' | 'favorites',
  mine = false,
  /** ⭐ De QUI on regarde les favoris. Absent ⇒ les siens. N'a de sens qu'avec view='favorites'. */
  favoritesOwnerId?: string | null,
) {
  const params = new URLSearchParams();
  if (status) params.set('status', status);
  // F11 — « à moi ou à personne ». The identity is the authenticated user, resolved server-side.
  if (mine) params.set('mine', 'true');
  // Honored server-side only for admins (the queue is Uniticket-only otherwise — see the route).
  if (includeTest) params.set('includeTest', 'true');
  // Free-text search on jvid / ticket number / remark (comment).
  if (search && search.trim()) params.set('search', search.trim());
  // Dedicated views: « Résolu (Uniticket) » + « À traiter par moi » (server-scoped to the caller).
  if (view) params.set('view', view);
  if (view === 'favorites' && favoritesOwnerId) params.set('favoritesOwnerId', favoritesOwnerId);
  const q = params.toString();
  return apiFetch<{ data: AddressValidationQueueItem[]; meta?: { isEscalationTarget?: boolean } }>(
    `/address-validations${q ? `?${q}` : ''}`,
  );
}

// ── Anti-collision validation lock (user ask 2026-07-30) ─────────────────────
// The window ACQUIRES the ticket's lock on mount and renews it every 60 s (same call — idempotent
// for the holder, TTL 3 min server-side). When someone else holds it the response carries their
// name: the window shows the red banner and disables its actions (the server enforces with 423
// regardless). Released on leave via a keepalive fetch (NOT sendBeacon — it can't carry the Bearer).

export interface ValidationLockState {
  held: boolean;
  holder?: { name: string; since: string };
  expiresAt?: string;
}

export async function acquireValidationLock(ticketId: string) {
  return apiFetch<{ data: ValidationLockState }>(`/tickets/${ticketId}/lock`, { method: 'POST' });
}

/** Fire-and-forget release on page leave/unmount — keepalive survives the unload; a missed release
 *  is covered by the server-side TTL. A non-holder's release is a harmless no-op. */
export function releaseValidationLockOnLeave(ticketId: string) {
  const token = getAccessToken();
  void fetch(`${API_PREFIX}/tickets/${ticketId}/lock/release`, {
    method: 'POST',
    keepalive: true,
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
  }).catch(() => {});
}

/** Admin ghost-lock break: force-release whoever holds it (server re-checks the admin role). */
export async function forceReleaseValidationLock(ticketId: string) {
  return apiFetch<{ data: { released: boolean } }>(`/tickets/${ticketId}/lock?force=true`, { method: 'DELETE' });
}

/** No-address mode: post a question to the operator on a ticket WITHOUT a validation row (the
 *  verdict panel — and its question field — is hidden there). Public Uniticket comment + ticket-level
 *  round-trip stamp (the sync flags the reply on the synthetic queue row). */
export async function askOperatorForTicket(
  ticketId: string,
  question: string,
  opts: { internal?: boolean; mentionedUserIds?: string[] } = {},
) {
  return apiFetch<{ data: { uniticketSync: UniticketWriteResult } }>(`/tickets/${ticketId}/ask-operator`, {
    method: 'POST',
    body: JSON.stringify({
      question,
      ...(opts.internal ? { internal: true } : {}),
      ...(opts.mentionedUserIds?.length ? { mentionedUserIds: opts.mentionedUserIds } : {}),
    }),
  });
}

/** No-address mode hand-offs (ticket-level — no validation row): same Uniticket write-backs as the
 *  row-level actions; the synthetic queue row moves to « En attente de réponse » / « À traiter ». */
export async function ticketSendToBuild(ticketId: string, remark?: string | null) {
  return apiFetch<{ data: { uniticketSync: UniticketWriteResult } }>(`/tickets/${ticketId}/ticket-send-to-build`, {
    method: 'POST',
    body: JSON.stringify({ remark: remark ?? null }),
  });
}
export async function ticketEscalate(ticketId: string, remark?: string | null) {
  return apiFetch<{ data: { uniticketSync: UniticketWriteResult } }>(`/tickets/${ticketId}/ticket-escalate`, {
    method: 'POST',
    body: JSON.stringify({ remark: remark ?? null }),
  });
}
/** …and hand it back down to whoever escalated it, with the answer (F4, no-jvid side). */
export async function ticketReturnEscalation(ticketId: string, remark?: string | null) {
  return apiFetch<{ data: { uniticketSync: UniticketWriteResult } }>(`/tickets/${ticketId}/ticket-escalation-return`, {
    method: 'POST',
    body: JSON.stringify({ remark: remark ?? null }),
  });
}

/** The ticket's public discussion thread (read-only; comments + system changes, oldest first). */
// Manual queue move (kebab menu in the window header) — local only, nothing posted to Uniticket… sauf, depuis
// N40, la réouverture d'un ticket résolu ou fermé quand on range le dossier « en attente » (`uniticketReopen`).
export async function reclassifyTicket(
  ticketId: string,
  target: 'WAITING_BUILD' | 'WAITING_OPERATOR' | 'PENDING',
) {
  return apiFetch<{ data: { scope: 'row' | 'ticket'; uniticketReopen?: UniticketReopenReport } }>(`/tickets/${ticketId}/reclassify`, {
    method: 'POST',
    body: JSON.stringify({ target }),
  });
}

export async function fetchTicketUpdates(ticketId: string) {
  return apiFetch<{ data: TicketUpdateItem[] }>(`/tickets/${ticketId}/updates`);
}

/** Append-only audit trail for this address (jvid+operator): who validated/corrected what, when. */
export async function fetchValidationHistory(ticketId: string, jvid?: number | null) {
  return apiFetch<{ data: AuditLogItem[] }>(`/tickets/${ticketId}/address-validation/history${jvidQs(jvid)}`);
}

/** N31 — « Délier » (admins) : l'aperçu, en lecture seule — ce qui serait défait, ou pourquoi c'est refusé. */
export async function fetchUnlinkPreview(ticketId: string) {
  return apiFetch<{ data: UnlinkPreview }>(`/tickets/${ticketId}/unlink-preview`);
}

/** N31 — le geste : le ticket repasse sans adresse. Motif obligatoire ; un refus apparu depuis l'aperçu rend un 409. */
export async function unlinkTicket(ticketId: string, reason: string) {
  return apiFetch<{ data: UnlinkTicketResult }>(`/tickets/${ticketId}/unlink-address`, {
    method: 'POST',
    body: JSON.stringify({ reason }),
  });
}
